import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { NamedLink } from '../../../components';
import { getStrapiMediaUrl } from '../../../util/strapiApi';
import {
  ALL_STATES_KEY,
  compareByFeaturedAndSortOrder,
  getStateKey,
  matchesSelectedState,
} from './communityStateFilter';
import MarketplaceSectionHeader from './MarketplaceSectionHeader';

import css from './Section.module.css';

const INITIAL_CARD_ROWS = 3;

const getCardsPerRow = () => {
  if (typeof window === 'undefined') {
    return 4;
  }

  const width = window.innerWidth;
  if (width >= 1280) return 4;
  if (width >= 1024) return 3;
  if (width >= 768) return 2;
  return 1;
};

const resolveInstructorState = instructor => {
  const relationState =
    instructor?.state && !Array.isArray(instructor.state) ? instructor.state : null;

  return {
    id: relationState?.id ?? instructor?.stateId ?? null,
    slug: relationState?.slug || instructor?.stateSlug || '',
    name:
      relationState?.name ||
      instructor?.stateName ||
      instructor?.locationState ||
      instructor?.state ||
      '',
  };
};

const getInstructorServiceArea = instructor => instructor?.title || '';

const getStateSortLabel = state => (state?.name || state?.slug || '').toString().trim();

const compareStatesAlphabetically = (a, b) => {
  return getStateSortLabel(a).localeCompare(getStateSortLabel(b), undefined, {
    sensitivity: 'base',
  });
};

const ImageWithFallback = ({ src, alt, wrapperClassName, imageClassName }) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div className={classNames(css.imageFallback, wrapperClassName)} aria-label={alt}>
        <div className={css.imageFallbackFill}>
          <span className={css.imageFallbackLabel}>No image</span>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <img
        src={src}
        alt={alt}
        className={imageClassName}
        loading="lazy"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

const SkeletonInstructorCard = () => (
  <div className={css.skeletonCard}>
    <div className={classNames(css.skeletonBlock, css.skeletonImageSquare)} />
    <div className={classNames(css.skeletonBlock, css.skeletonLineShort)} />
    <div className={classNames(css.skeletonBlock, css.skeletonLine)} />
    <div className={classNames(css.skeletonBlock, css.skeletonLine)} />
    <div className={classNames(css.skeletonBlock, css.skeletonButton)} />
  </div>
);

const HomepageCoachesSection = props => {
  const {
    states = [],
    instructors = [],
    isLoading,
    hasError,
    sectionId = 'instructors-by-state',
    title = 'Instructors by State',
    description = 'Browse states and find instructors near you.',
    headerAction = null,
  } = props;
  const [selectedStateKey, setSelectedStateKey] = useState(ALL_STATES_KEY);
  const [instructorCardsExpanded, setInstructorCardsExpanded] = useState(false);
  const [cardsPerRow, setCardsPerRow] = useState(getCardsPerRow);

  const orderedInstructors = useMemo(() => {
    return [...instructors].filter(Boolean).sort(compareByFeaturedAndSortOrder);
  }, [instructors]);

  const orderedStates = useMemo(() => {
    const stateKeysWithInstructors = new Set();

    orderedInstructors.forEach(instructor => {
      const key = getStateKey(resolveInstructorState(instructor));
      if (key) {
        stateKeysWithInstructors.add(key);
      }
    });

    const derivedStates = orderedInstructors
      .map(resolveInstructorState)
      .filter(state => getStateKey(state));
    const seen = new Set();

    return [...states, ...derivedStates]
      .filter(Boolean)
      .filter(state => {
        const key = getStateKey(state);
        if (!key || seen.has(key) || !stateKeysWithInstructors.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      })
      .sort(compareStatesAlphabetically);
  }, [orderedInstructors, states]);

  useEffect(() => {
    if (selectedStateKey === ALL_STATES_KEY) return;

    const hasSelectedState = orderedStates.some(state => getStateKey(state) === selectedStateKey);
    if (!hasSelectedState) {
      setSelectedStateKey(ALL_STATES_KEY);
    }
  }, [orderedStates, selectedStateKey]);

  useEffect(() => {
    const updateCardsPerRow = () => {
      setCardsPerRow(getCardsPerRow());
    };

    updateCardsPerRow();
    window.addEventListener('resize', updateCardsPerRow);

    return () => {
      window.removeEventListener('resize', updateCardsPerRow);
    };
  }, []);

  const selectedState = useMemo(
    () => orderedStates.find(state => getStateKey(state) === selectedStateKey) || null,
    [orderedStates, selectedStateKey]
  );

  const filteredInstructors = useMemo(() => {
    if (selectedStateKey === ALL_STATES_KEY || !selectedState) {
      return orderedInstructors;
    }

    return orderedInstructors.filter(instructor =>
      matchesSelectedState(selectedState, resolveInstructorState(instructor))
    );
  }, [orderedInstructors, selectedState, selectedStateKey]);

  const instructorCards = useMemo(() => {
    return filteredInstructors.map(instructor => {
      const instructorState = resolveInstructorState(instructor);
      const stateLabel = instructorState?.name || '';

      return {
        key: instructor?.id || instructor?.slug || instructor?.name,
        name: instructor?.name || '',
        slug: instructor?.slug || '',
        stateLabel,
        serviceArea: getInstructorServiceArea(instructor),
        profileImage: getStrapiMediaUrl(instructor?.profileImage || instructor?.coverImage),
      };
    });
  }, [filteredInstructors]);

  useEffect(() => {
    setInstructorCardsExpanded(false);
  }, [selectedStateKey, instructorCards.length]);

  const showGlobalEmptyState = !isLoading && !hasError && orderedInstructors.length === 0;
  const showFilteredEmptyState =
    !isLoading &&
    !hasError &&
    selectedStateKey !== ALL_STATES_KEY &&
    filteredInstructors.length === 0;
  const collapsedInstructorCardLimit = cardsPerRow * INITIAL_CARD_ROWS;
  const showInstructorCardsToggle = instructorCards.length > collapsedInstructorCardLimit;
  const visibleInstructorCards = instructorCardsExpanded
    ? instructorCards
    : instructorCards.slice(0, collapsedInstructorCardLimit);

  return (
    <section id={sectionId} className={classNames(css.marketplaceSection, css.sectionPadding)}>
      <div className={css.fixedWidthContainer}>
        <MarketplaceSectionHeader
          title={title}
          description={description}
          headerAction={headerAction}
        />

        {isLoading ? (
          <>
            <div className={css.chipRow}>
              {Array.from({ length: 6 }).map((_, index) => (
                <span
                  key={`state-pill-skeleton-${index}`}
                  className={classNames(css.skeletonBlock, css.skeletonPill)}
                />
              ))}
            </div>
            <div className={css.cardsGrid}>
              {Array.from({ length: 4 }).map((_, index) => (
                <SkeletonInstructorCard key={`instructor-skeleton-${index}`} />
              ))}
            </div>
          </>
        ) : hasError ? (
          <div className={css.marketplaceEmptyState}>
            <h3>Unable to load instructors</h3>
            <p>Please refresh the page in a moment.</p>
          </div>
        ) : showGlobalEmptyState ? (
          <div className={css.marketplaceEmptyState}>
            <h3>No instructors available yet</h3>
            <p>Add instructors in Strapi to display them here.</p>
          </div>
        ) : (
          <>
            <div
              className={classNames(css.chipRow, css.stateChipRow)}
              role="toolbar"
              aria-label="Filter instructors by state"
              tabIndex={0}
            >
              <button
                type="button"
                className={classNames(css.chip, {
                  [css.chipActive]: selectedStateKey === ALL_STATES_KEY,
                })}
                aria-pressed={selectedStateKey === ALL_STATES_KEY}
                onClick={() => setSelectedStateKey(ALL_STATES_KEY)}
              >
                All
              </button>

              {orderedStates.map(state => {
                const stateKey = getStateKey(state);
                if (!stateKey) return null;

                return (
                  <button
                    key={stateKey}
                    type="button"
                    className={classNames(css.chip, {
                      [css.chipActive]: selectedStateKey === stateKey,
                    })}
                    aria-pressed={selectedStateKey === stateKey}
                    onClick={() => setSelectedStateKey(stateKey)}
                  >
                    {state?.name || 'Unnamed state'}
                  </button>
                );
              })}
            </div>

            {showFilteredEmptyState ? (
              <div className={css.marketplaceEmptyState}>
                <h3>No instructors found in {selectedState?.name || 'this state'} yet.</h3>
                <p>Try another state or check back soon.</p>
              </div>
            ) : (
              <>
                <div className={css.cardsGrid}>
                  {visibleInstructorCards.map(card => {
                    const canOpenInstructor = Boolean(card.slug);
                    const cardContent = (
                      <>
                        <ImageWithFallback
                          src={card.profileImage}
                          alt={card.name || 'Instructor profile'}
                          wrapperClassName={css.coachImageWrapper}
                          imageClassName={css.cardImage}
                        />

                        <div className={css.cardBody}>
                          {card.stateLabel ? (
                            <span className={css.instructorStateLabel}>{card.stateLabel}</span>
                          ) : null}

                          <h3 className={css.cardTitle}>{card.name || 'Instructor'}</h3>
                          <p className={css.cardSubtitle}>
                            {card.serviceArea || 'Areas served coming soon.'}
                          </p>

                          <span
                            className={classNames(css.cardCta, {
                              [css.cardCtaDisabled]: !canOpenInstructor,
                            })}
                          >
                            View Instructor
                          </span>
                        </div>
                      </>
                    );

                    return canOpenInstructor ? (
                      <NamedLink
                        key={card.key}
                        className={css.coachCard}
                        name="InstructorPage"
                        params={{ slug: card.slug }}
                      >
                        {cardContent}
                      </NamedLink>
                    ) : (
                      <article key={card.key} className={css.coachCard}>
                        {cardContent}
                      </article>
                    );
                  })}
                </div>
                {showInstructorCardsToggle ? (
                  <div className={css.listingsToggleRow}>
                    <button
                      type="button"
                      className={css.listingsToggleButton}
                      aria-expanded={instructorCardsExpanded}
                      onClick={() => setInstructorCardsExpanded(expanded => !expanded)}
                    >
                      {instructorCardsExpanded ? 'Show Less' : 'Show More'}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default HomepageCoachesSection;
